import React, { useEffect, useMemo, useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate } from "react-router-dom";
import {
  useOffersListStore,
  selectHasPendingChanges,
  type OfferSummary,
} from "../../store/useOffersListStore";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { NavigationBlocker } from "../../components/common/NavigationBlocker";
import { PageBreadcrumbs } from "../../components/common/PageBreadcrumbs";
import { ListSearchBar } from "../../components/shared/ListSearchBar";
import { SalesBoosterTabBar } from "./SalesBoosterTabBar";
import { ListEmptySearchResults } from "../../components/shared/ListEmptySearchResults";

// ─── Sub-components ───────────────────────────────────────────────────────────

function DownsellsTable({
  downsells,
  allDownsellsCount,
  hasPendingChanges,
  searchQuery,
  onSearchChange,
  onEdit,
  onAdd,
  onToggleActive,
  onDelete,
  onDuplicate,
}: {
  downsells: OfferSummary[];
  allDownsellsCount: number;
  hasPendingChanges: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onEdit: (id: string) => void;
  onAdd: () => void;
  onToggleActive: (id: string, next: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}) {
  if (allDownsellsCount === 0) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="discount" />
          <s-heading>
            <strong>Crea tu primer downsell</strong>
          </s-heading>
          <s-text color="subdued">
            Los downsells se muestran cuando el cliente intenta cerrar el
            formulario. Úsalos para ofrecer un descuento y recuperar clientes
            que estaban a punto de abandonar.
          </s-text>
          <s-button variant="primary" icon="plus" onClick={onAdd}>
            Añadir downsell
          </s-button>
        </s-stack>
      </s-box>
    );
  }

  if (downsells.length === 0 && !searchQuery && hasPendingChanges) {
    return (
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-400"
      >
        <s-stack direction="block" gap="base" alignItems="center">
          <s-icon type="discount" />
          <s-text color="subdued">
            Todos los downsells están marcados para eliminar.
          </s-text>
          <s-text color="subdued">
            Guarda para confirmar o descarta los cambios para restaurarlos.
          </s-text>
        </s-stack>
      </s-box>
    );
  }

  return (
    <>
      <ListSearchBar value={searchQuery} onChange={onSearchChange} />

      {downsells.length === 0 ? (
        <ListEmptySearchResults onClear={() => onSearchChange("")}>
          Ningún downsell coincide con tu búsqueda.
        </ListEmptySearchResults>
      ) : (
        <s-table>
          <s-table-header-row>
            <s-table-header>Activo</s-table-header>
            <s-table-header>Nombre</s-table-header>
            <s-table-header>Acciones</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {downsells.map((downsell) => (
              <s-table-row key={downsell.id}>
                <s-table-cell>
                  <s-switch
                    label="Activar downsell"
                    labelAccessibilityVisibility="exclusive"
                    checked={downsell.is_active}
                    onChange={(e: CallbackEvent<"s-switch">) =>
                      onToggleActive(downsell.id, e.currentTarget.checked)
                    }
                  />
                </s-table-cell>
                <s-table-cell>{downsell.internal_name}</s-table-cell>
                <s-table-cell>
                  <s-stack direction="inline" gap="small-200">
                    <s-button
                      variant="tertiary"
                      icon="edit"
                      onClick={() => onEdit(downsell.id)}
                    />
                    <s-button
                      variant="tertiary"
                      icon="duplicate"
                      onClick={() => onDuplicate(downsell.id)}
                    />
                    <s-button
                      variant="tertiary"
                      icon="delete"
                      tone="critical"
                      onClick={() => onDelete(downsell.id)}
                    />
                  </s-stack>
                </s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
      )}
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const DownsellsPage: React.FC = () => {
  const navigate = useNavigate();

  // Gate by kind so we don't briefly render rows from another list
  // page on the first paint after navigation. See UpsellsOneTickPage
  // for the full explanation.
  const offers = useOffersListStore((s) =>
    s.query.kind === "downsell" ? s.offers : []
  );
  const pendingDeleteIds = useOffersListStore((s) => s.pendingDeleteIds);
  const isLoading = useOffersListStore((s) => s.isLoading);
  const isCommitting = useOffersListStore((s) => s.isCommitting);
  const error = useOffersListStore((s) => s.error);
  const isAwaitingOurKind = useOffersListStore(
    (s) => s.query.kind !== "downsell" || s.lastFetchedAt === null
  );
  const loadOffers = useOffersListStore((s) => s.loadOffers);
  const toggleActive = useOffersListStore((s) => s.toggleActive);
  const duplicateOffer = useOffersListStore((s) => s.duplicateOffer);
  const stageDelete = useOffersListStore((s) => s.stageDelete);
  const commitPending = useOffersListStore((s) => s.commitPending);
  const discardPending = useOffersListStore((s) => s.discardPending);

  const hasPendingChanges = useOffersListStore(selectHasPendingChanges);

  const visibleOffers = useMemo(() => {
    if (pendingDeleteIds.length === 0) return offers;
    const pending = new Set(pendingDeleteIds);
    return offers.filter((o) => !pending.has(o.id));
  }, [offers, pendingDeleteIds]);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadOffers({ kind: "downsell" });
  }, [loadOffers]);

  const searchedOffers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleOffers;
    return visibleOffers.filter((o) =>
      o.internal_name.toLowerCase().includes(q)
    );
  }, [visibleOffers, searchQuery]);

  const handleEdit = (id: string) => navigate(`/sales-booster/downsells/${id}`);
  const handleAdd = () => navigate("/sales-booster/downsells/new");

  const handleToggleActive = async (id: string, next: boolean) => {
    try {
      await toggleActive(id, next);
      window.shopify?.toast?.show(
        next ? "Downsell activado" : "Downsell desactivado"
      );
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudo cambiar el estado del downsell";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  const handleDelete = (id: string) => stageDelete(id);

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateOffer(id);
      window.shopify?.toast?.show(
        "Downsell duplicado como borrador inactivo. Revísalo y actívalo cuando estés listo."
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "No se pudo duplicar el downsell";
      window.shopify?.toast?.show(message, { isError: true });
    }
  };

  useShopifySaveBar({
    isDirty: hasPendingChanges,
    isSaving: isCommitting,
    onSave: async () => {
      await commitPending();
    },
    onDiscard: discardPending,
  });

  const showInitialLoading = isLoading || isAwaitingOurKind;

  return (
    <div>
      <NavigationBlocker isDirty={hasPendingChanges} />

      <s-page heading="Downsells">
        <PageBreadcrumbs
          items={[{ label: "Impulsor de ventas", to: "/sales-booster" }]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {error && !showInitialLoading && (
            <s-banner tone="critical">
              <s-text>{error}</s-text>
            </s-banner>
          )}

          <s-stack
            direction="inline"
            alignItems="center"
            justifyContent="space-between"
          >
            <s-box paddingBlockEnd="small-200">
              <s-heading>
                <strong>Tus downsells</strong>
              </s-heading>
            </s-box>
            <s-button variant="primary" icon="plus" onClick={handleAdd}>
              Añadir downsell
            </s-button>
          </s-stack>

          {showInitialLoading ? (
            <s-box
              background="base"
              border="small subdued solid"
              borderRadius="large-100"
              padding="large-400"
            >
              <s-stack direction="block" gap="base" alignItems="center">
                <s-text color="subdued">Cargando downsells…</s-text>
              </s-stack>
            </s-box>
          ) : (
            <s-section padding="none">
              <DownsellsTable
                downsells={searchedOffers}
                allDownsellsCount={offers.length}
                hasPendingChanges={hasPendingChanges}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onEdit={handleEdit}
                onAdd={handleAdd}
                onToggleActive={handleToggleActive}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
              />
            </s-section>
          )}
        </s-stack>
      </s-page>
    </div>
  );
};

export default DownsellsPage;
